import React from 'react'
import styled, { css } from 'styled-components'

/** Forecast vs ordered balance — drives the optional cell tint via theme tokens. */
export type BalanceState = 'over' | 'under' | undefined

type Props = {
  sticky?: boolean
  /** RTL sticky offset in px from the right edge. */
  offset?: number
  /** Edge shadow on the last frozen column, against the scrolling area. */
  lastFrozen?: boolean
  /** Balance highlight: 'over' → dangerSoft, 'under' → warningSoft. */
  state?: BalanceState
  /** Subtle greenish base (grid-table hover tone) for expandable grower rows. */
  tint?: boolean
  width?: string
  title?: string
  onClick?: (e: React.MouseEvent<HTMLTableCellElement>) => void
  children?: React.ReactNode
}

const SellerBodyCell = ({
  sticky,
  offset = 0,
  lastFrozen,
  state,
  tint,
  width,
  title,
  onClick,
  children,
}: Props) => (
  <Td
    $sticky={!!sticky}
    $offset={offset}
    $lastFrozen={!!lastFrozen}
    $state={state}
    $tint={!!tint}
    $width={width}
    $clickable={!!onClick}
    title={title}
    onClick={onClick}
  >
    {children}
  </Td>
)

export default SellerBodyCell

const Td = styled.td<{
  $sticky: boolean
  $offset: number
  $lastFrozen: boolean
  $state: BalanceState
  $tint: boolean
  $width?: string
  $clickable: boolean
}>`
  padding: 0 10px;
  height: 44px;
  font-size: ${({ theme }) => theme.text.lg};
  color: ${({ theme }) => theme.colors.ink2};
  border-bottom: 1px solid ${({ theme }) => theme.colors.line2};
  white-space: nowrap;
  vertical-align: middle;
  text-align: center;
  background: ${({ theme, $state, $tint }) =>
    $state === 'over'
      ? theme.colors.dangerSoft
      : $state === 'under'
        ? theme.colors.warningSoft
        : $tint
          ? theme.colors.bgLayout
          : theme.colors.surface};
  ${({ $clickable }) => $clickable && css`cursor: pointer;`}
  ${({ $width }) =>
    $width &&
    css`
      width: ${$width};
      min-width: ${$width};
    `}
  ${({ $sticky }) =>
    $sticky &&
    css`
      /* Manual freeze instead of position: sticky (unreliable on iOS Safari).
         The frozen columns are the two physical-right (RTL leading) cells; on
         horizontal scroll they translateX by --freeze-x (set on the scroll
         container) to stay pinned to the right edge. Opaque background +
         z-index keep them above the scrolled-under cells. */
      position: relative;
      z-index: 1;
      transform: translateX(var(--freeze-x, 0px));
      will-change: transform;
    `}
  ${({ $lastFrozen }) =>
    $lastFrozen &&
    css`
      box-shadow: -6px 0 6px -4px rgba(0, 0, 0, 0.12);
    `}
`
